// Shared phonics content: each letter maps to picture items whose name starts
// with that letter's sound. Games draw targets + distractors from here.
export const ITEMS = {
  m: [ { label: "Milk", emoji: "🥛" }, { label: "Moon", emoji: "🌙" }, { label: "Mouse", emoji: "🐭" }, { label: "Mushroom", emoji: "🍄" } ],
  s: [ { label: "Sun", emoji: "☀️" }, { label: "Sock", emoji: "🧦" }, { label: "Snake", emoji: "🐍" }, { label: "Star", emoji: "⭐" } ],
  a: [ { label: "Apple", emoji: "🍎" }, { label: "Ant", emoji: "🐜" }, { label: "Alligator", emoji: "🐊" } ],
  t: [ { label: "Tiger", emoji: "🐯" }, { label: "Tent", emoji: "⛺" }, { label: "Tomato", emoji: "🍅" }, { label: "Turtle", emoji: "🐢" } ],
  p: [ { label: "Pizza", emoji: "🍕" }, { label: "Pig", emoji: "🐷" }, { label: "Pen", emoji: "🖊️" }, { label: "Pancake", emoji: "🥞" } ],
  i: [ { label: "Igloo", emoji: "🧊" }, { label: "Iguana", emoji: "🦎" }, { label: "Insect", emoji: "🐛" } ],
  n: [ { label: "Nest", emoji: "🪺" }, { label: "Nose", emoji: "👃" }, { label: "Nut", emoji: "🥜" }, { label: "Net", emoji: "🥅" } ],
  d: [ { label: "Dog", emoji: "🐶" }, { label: "Duck", emoji: "🦆" }, { label: "Donut", emoji: "🍩" }, { label: "Dice", emoji: "🎲" } ],
  f: [ { label: "Fish", emoji: "🐟" }, { label: "Fire", emoji: "🔥" }, { label: "Foot", emoji: "🦶" }, { label: "Fries", emoji: "🍟" } ],
  o: [ { label: "Octopus", emoji: "🐙" }, { label: "Ostrich", emoji: "🦤" }, { label: "Olive", emoji: "🫒" }, { label: "Ox", emoji: "🐂" } ],
  r: [ { label: "Rocket", emoji: "🚀" }, { label: "Ring", emoji: "💍" }, { label: "Robot", emoji: "🤖" }, { label: "Rainbow", emoji: "🌈" } ],
  h: [ { label: "House", emoji: "🏠" }, { label: "Hat", emoji: "🎩" }, { label: "Heart", emoji: "❤️" }, { label: "Hamster", emoji: "🐹" } ],
  b: [ { label: "Ball", emoji: "⚽" }, { label: "Banana", emoji: "🍌" }, { label: "Bee", emoji: "🐝" }, { label: "Bus", emoji: "🚌" } ],
  l: [ { label: "Lion", emoji: "🦁" }, { label: "Leaf", emoji: "🍃" }, { label: "Ladder", emoji: "🪜" }, { label: "Lemon", emoji: "🍋" } ],
  c: [ { label: "Cake", emoji: "🎂" }, { label: "Car", emoji: "🚗" }, { label: "Cat", emoji: "🐱" }, { label: "Carrot", emoji: "🥕" } ],
  g: [ { label: "Gift", emoji: "🎁" }, { label: "Goat", emoji: "🐐" }, { label: "Grapes", emoji: "🍇" }, { label: "Ghost", emoji: "👻" } ],
  u: [ { label: "Umbrella", emoji: "☂️" }, { label: "Unicorn", emoji: "🦄" }, { label: "UFO", emoji: "🛸" } ],
  e: [ { label: "Egg", emoji: "🥚" }, { label: "Elephant", emoji: "🐘" }, { label: "Envelope", emoji: "✉️" } ],
};

export function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function itemFor(letter) {
  const list = ITEMS[letter] || [{ label: letter, emoji: "❓" }];
  return { ...pick(list), letter };
}

// Build N option items: one matching `letter`, rest from other letters.
export function buildOptions(letter, allLetters, n = 3) {
  const correct = itemFor(letter);
  const others = allLetters.filter((l) => l !== letter);
  const distractors = [];
  const usedLetters = new Set([letter]);
  while (distractors.length < n - 1 && others.length) {
    const l = pick(others.filter((x) => !usedLetters.has(x)).length ? others.filter((x) => !usedLetters.has(x)) : others);
    usedLetters.add(l);
    distractors.push(itemFor(l));
    if (usedLetters.size > 20) break;
  }
  return shuffle([correct, ...distractors]);
}

export function buildLetterTiles(letter, allLetters, n = 3) {
  const others = shuffle(allLetters.filter((l) => l !== letter)).slice(0, n - 1);
  return shuffle([letter, ...others]);
}

// --- Content for older age bands ---
export const CVC_WORDS = [
  { word: "cat", emoji: "🐱" }, { word: "pig", emoji: "🐷" }, { word: "sun", emoji: "☀️" },
  { word: "dog", emoji: "🐶" }, { word: "hat", emoji: "🎩" }, { word: "bed", emoji: "🛏️" },
  { word: "map", emoji: "🗺️" }, { word: "cup", emoji: "☕" }, { word: "bus", emoji: "🚌" },
  { word: "net", emoji: "🥅" },
];

export const DIGRAPH_ROUNDS = [
  { target: "sh", word: "ship", emoji: "🚢", before: "", after: "ip", options: ["sh", "ch", "th"] },
  { target: "ch", word: "chair", emoji: "🪑", before: "", after: "air", options: ["ch", "sh", "st"] },
  { target: "th", word: "thumb", emoji: "👍", before: "", after: "umb", options: ["th", "sh", "bl"] },
  { target: "st", word: "star", emoji: "⭐", before: "", after: "ar", options: ["st", "sh", "ch"] },
  { target: "bl", word: "block", emoji: "🧱", before: "", after: "ock", options: ["bl", "st", "th"] },
];

export const SIGHT_WORDS = ["the", "said", "was", "you", "are", "have", "see", "like"];

export function cvcEmoji(word) {
  const f = CVC_WORDS.find((w) => w.word === word);
  return f ? f.emoji : "❓";
}
